import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchOrganizer,
  fetchOrganizerPaymentPoints,
  fetchOrganizerUsers,
  fetchOrganizerVenues,
} from '../redux/slices/organizersSlice';
import { fetchPaymentPoints } from '../redux/slices/paymentPointsSlice';
import { fetchUsers } from '../redux/slices/usersSlice';
import { fetchVenues } from '../redux/slices/venuesSlice';
import { useTheme, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TabPanel from '../components/TabPanel';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import ListItemText from '@mui/material/ListItemText';
import CloseIcon from '@mui/icons-material/Close';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import OrganizerDrawer from '../components/OrganizerDrawer';
import api from '../api';

function LinkListUser({ organizerId, id, first_name, last_name, email, isLinked }) {
  const [isChecked, setIsChecked] = useState(false);
  const [checkedDisabled, setCheckedDisabled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setIsChecked(isLinked);
  }, [isLinked]);

  const onChange = (event) => {
    const checked = event.target.checked;
    setCheckedDisabled(true);
    api
      .patch(`/organizers/${organizerId}/users`, { user_id: id, action: checked ? 'link' : 'unlink' })
      .then(() => {
        setIsChecked(checked);
        setCheckedDisabled(false);
      })
      .catch(() => {
        toast.error(t('An error occured, please try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setCheckedDisabled(false);
      });
  };

  return (
    <ListItem key={id} divider>
      <ListItemText primary={`${first_name} ${last_name}`} secondary={email} />
      <ListItemSecondaryAction>
        <Checkbox disabled={checkedDisabled} checked={isChecked} onChange={onChange} />
      </ListItemSecondaryAction>
    </ListItem>
  );
}

function LinkUserDialog({ open, onClose, organizer }) {
  const { t } = useTranslation();

  const onCloseInternal = () => {
    onClose();
  };

  const dispatch = useDispatch();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const usersError = useSelector((state) => state.users.error);

  const users = useSelector((state) => {
    return state.users.data.map((user) => {
      const isLinked = !!state.organizers.organizerUsers.find((linkedUser) => linkedUser.users_id === user.id);
      return { ...user, isLinked };
    });
  });

  useEffect(() => {
    if (usersError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [usersError, t]);

  useEffect(() => {
    if (open) {
      dispatch(fetchUsers({ role: 'ORGANIZER' }));
      // TODO: what happens here if it fails?!
      dispatch(fetchOrganizerUsers({ organizerId: organizer.id }));
    }
  }, [dispatch, open, organizer]);

  return (
    <Dialog fullScreen={fullScreen} open={open} onClose={onClose}>
      <DialogTitle gutterBottom>
        {organizer.name} {t('Users')}
        <IconButton
          aria-label="close"
          onClick={onCloseInternal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: 0 }}>
        {users.length ? (
          <List sx={{ minHeight: 500, minWidth: 400, overflow: 'auto' }}>
            {users.map((user) => (
              <LinkListUser key={user.id} organizerId={organizer.id} {...user} />
            ))}
          </List>
        ) : (
          <Box sx={{ minHeight: 500, minWidth: 400 }} display="flex" alignItems="center" justifyContent="center">
            <Typography sx={{ color: '#888888' }} variant="h6">
              {t('No data to display')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LinkListVenue({ organizerId, id, name, city, isLinked, isOwner }) {
  const [isChecked, setIsChecked] = useState({
    first: false,
    second: false,
  });
  const [checkedDisabled, setCheckedDisabled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setIsChecked({ first: isLinked, second: isOwner });
  }, [isLinked, isOwner]);

  const onChangeAccess = (event) => {
    const { name, checked } = event.target;
    setCheckedDisabled(true);
    api
      .patch(`/organizers/${organizerId}/venues`, {
        venue_id: id,
        action: checked ? 'link' : 'unlink',
      })
      .then(() => {
        setIsChecked((prev) => ({
          ...prev,
          [name]: checked,
        }));
        if (name === 'first' && !checked)
          setIsChecked({
            first: checked,
            second: false,
          });
        setCheckedDisabled(false);
      })
      .catch(() => {
        toast.error(t('An error occured, please try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setCheckedDisabled(false);
      });
  };

  const onChangeOwner = (event) => {
    const { name, checked } = event.target;
    api
      .patch(`/organizers/${organizerId}/owner`, {
        venue_id: id,
        is_owner: checked,
      })
      .then(() => {
        setIsChecked((prev) => ({
          ...prev,
          [name]: checked,
        }));
      })
      .catch(() => {
        toast.error(t('An error occured, please try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      });
  };

  return (
    <ListItem key={id} divider>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <Box>
          <Typography sx={{ maxWidth: 100 }}>{name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {city}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 5 }}>
          <Checkbox name="first" disabled={checkedDisabled} checked={isChecked.first} onChange={onChangeAccess} />
          <Checkbox name="second" disabled={!isChecked.first} checked={isChecked.second} onChange={onChangeOwner} />
        </Box>
      </Box>
    </ListItem>
  );
}

function LinkVenueDialog({ open, onClose, organizer }) {
  const { t } = useTranslation();

  const onCloseInternal = () => {
    onClose();
  };

  const dispatch = useDispatch();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const venuesError = useSelector((state) => state.venues.error);

  const venues = useSelector((state) => {
    return state.venues.data.map((venue) => {
      const isLinked = !!state.organizers.organizerVenues.find((linkedVenue) => linkedVenue.venues_id === venue.id);
      const isOwner = venue.organizers_id === organizer.id;
      return { ...venue, isLinked, isOwner };
    });
  });

  useEffect(() => {
    if (venuesError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [venuesError, t]);

  useEffect(() => {
    if (open) {
      dispatch(fetchVenues());
      // TODO: what happens here if it fails?!
      dispatch(fetchOrganizerVenues({ organizerId: organizer.id }));
    }
  }, [dispatch, open, organizer]);

  return (
    <Dialog fullScreen={fullScreen} open={open} onClose={onClose}>
      <DialogTitle gutterBottom>
        {organizer.name} {t('Venues')}
        <IconButton
          aria-label="close"
          onClick={onCloseInternal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: 0 }}>
        {venues.length ? (
          <List sx={{ minHeight: 500, minWidth: 400 }}>
            <Box display="flex" justifyContent="space-between" sx={{ direction: 'rtl' }}>
              <Box sx={{ display: 'flex' }}>
                <Typography variant="h8" sx={{ px: 2, py: 1 }}>
                  {t('Owner')}
                </Typography>
                <Typography variant="h8" sx={{ px: 2, py: 1 }}>
                  {t('Access')}
                </Typography>
              </Box>
            </Box>
            {venues.map((venue) => (
              <LinkListVenue key={venue.id} organizerId={organizer.id} {...venue} />
            ))}
          </List>
        ) : (
          <Box sx={{ minHeight: 500, minWidth: 400 }} display="flex" alignItems="center" justifyContent="center">
            <Typography sx={{ color: '#888888' }} variant="h6">
              {t('No data to display')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LinkListPaymentPoint({ organizerId, id, name, city, isLinked }) {
  const [isChecked, setIsChecked] = useState(false);
  const [checkedDisabled, setCheckedDisabled] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setIsChecked(isLinked);
  }, [isLinked]);

  const onChange = (event) => {
    const checked = event.target.checked;
    setCheckedDisabled(true);
    api
      .patch(`/organizers/${organizerId}/paymentPoints`, { payment_point_id: id, action: checked ? 'link' : 'unlink' })
      .then(() => {
        setIsChecked(checked);
        setCheckedDisabled(false);
      })
      .catch(() => {
        toast.error(t('An error occured, please try again!'), {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
        setCheckedDisabled(false);
      });
  };

  return (
    <ListItem key={id} divider>
      <ListItemText primary={name} secondary={city} />
      <ListItemSecondaryAction>
        <Checkbox disabled={checkedDisabled} checked={isChecked} onChange={onChange} />
      </ListItemSecondaryAction>
    </ListItem>
  );
}

function LinkPaymentPointDialog({ open, onClose, organizer }) {
  const { t } = useTranslation();

  const onCloseInternal = () => {
    onClose();
  };

  const dispatch = useDispatch();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const paymentPointsError = useSelector((state) => state.paymentPoints.error);

  const paymentPoints = useSelector((state) => {
    return state.paymentPoints.data.map((paymentPoint) => {
      const isLinked = !!state.organizers.organizerPaymentPoints.find(
        (linkedPaymentPoint) => linkedPaymentPoint.id === paymentPoint.id
      );
      return { ...paymentPoint, isLinked };
    });
  });

  useEffect(() => {
    if (paymentPointsError) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [paymentPointsError, t]);

  useEffect(() => {
    if (open) {
      dispatch(fetchPaymentPoints());
      // TODO: what happens here if it fails?!
      dispatch(fetchOrganizerPaymentPoints({ organizerId: organizer.id }));
    }
  }, [dispatch, open, organizer]);

  return (
    <Dialog fullScreen={fullScreen} open={open} onClose={onClose}>
      <DialogTitle gutterBottom>
        {organizer.name} {t('Payment Points')}
        <IconButton
          aria-label="close"
          onClick={onCloseInternal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ padding: 0 }}>
        {paymentPoints.length ? (
          <List sx={{ minHeight: 500, minWidth: 400 }}>
            {paymentPoints.map((paymentPoint) => (
              <LinkListPaymentPoint key={paymentPoint.id} organizerId={organizer.id} {...paymentPoint} />
            ))}
          </List>
        ) : (
          <Box sx={{ minHeight: 500, minWidth: 400 }} display="flex" alignItems="center" justifyContent="center">
            <Typography sx={{ color: '#888888' }} variant="h6">
              {t('No data to display')}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function OrganizerDetails() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { organizerId } = useParams();
  const [editOrganizer, setEditOrganizer] = useState(null);
  const [openUsers, setOpenUsers] = useState(false);
  const [openVenues, setOpenVenues] = useState(false);
  const [openPaymentPoints, setOpenPaymentPoints] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);

  const organizer = useSelector((state) => state.organizers.selectedOrganizer);
  //const isLoading = useSelector((state) => state.organizers.isLoading);
  const error = useSelector((state) => state.organizers.error);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openEdit = useCallback(() => {
    setEditOrganizer(organizer);
    handleClose();
  }, [organizer]);

  const closeEdit = () => {
    setEditOrganizer(null);
  };

  const toggleUsers = () => setOpenUsers((openUsers) => !openUsers);
  const toggleVenues = () => setOpenVenues((openVenues) => !openVenues);
  const togglePaymentPoints = () => setOpenPaymentPoints((openPaymentPoints) => !openPaymentPoints);

  useEffect(() => {
    if (organizerId) {
      dispatch(fetchOrganizer({ organizerId }));
    }
  }, [dispatch, organizerId]);

  useEffect(() => {
    if (error) {
      toast.error(t('An error occured, please refresh the page and try again!'), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, [error, t]);

  const statusChip = useMemo(() => {
    const { status } = organizer;

    if (!status) return null;

    const chipColor = status === 'INACTIVE' ? 'error' : 'success';

    return (
      <Chip
        onClick={openEdit}
        sx={{ ml: 2, borderRadius: 1 }}
        size="small"
        label={t(status)}
        variant="outlined"
        color={chipColor}
      />
    );
  }, [organizer, openEdit, t]);

  return (
    <div className="organizer-details">
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Box display="flex" alignItems="center">
            <Typography variant="h3">{organizer.name}</Typography>
            {statusChip}
          </Box>
        </Grid>
        <Grid item>
          <IconButton onClick={handleMenu}>
            <MoreHorizIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={!!anchorEl}
            onClose={handleClose}
          >
            <MenuItem onClick={openEdit}>{t('Edit')}</MenuItem>
            <MenuItem
              onClick={() => {
                toggleUsers();
                handleClose();
              }}
            >
              {t('Users')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                toggleVenues();
                handleClose();
              }}
            >
              {t('Venues')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                togglePaymentPoints();
                handleClose();
              }}
            >
              {t('Payment Points')}
            </MenuItem>
          </Menu>
        </Grid>
      </Grid>
      <Box sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabIndex} onChange={(_, newValue) => setTabIndex(newValue)}>
          <Tab label={t('Details')} />
        </Tabs>
      </Box>
      <TabPanel value={tabIndex} index={0}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography gutterBottom variant="h5">
            {t('Pricings')}
          </Typography>
        </Grid>
        <TableContainer sx={{ marginTop: 2, marginBottom: 2 }} component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& > th': { whiteSpace: 'nowrap' } }}>
                <TableCell>{t('Platform Comission')}</TableCell>
                <TableCell>{t('Seat Price')}</TableCell>
                <TableCell>{t('Free Seat Price')}</TableCell>
                <TableCell>{t('Cancel Seat Price')}</TableCell>
                <TableCell>{t('Cashier Fixed Com.')}</TableCell>
                <TableCell>{t('Sms Price')}</TableCell>
                <TableCell>{t('Email Price')}</TableCell>
                <TableCell>{t('Unique Fiscal Serial')}</TableCell>
                <TableCell>{t('Price zero fiscalization')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow
                sx={{
                  '& > td': { whiteSpace: 'nowrap' },
                  '&:last-child td, &:last-child th': { borderBottom: 0 },
                  '&:nth-of-type(odd)': (theme) => ({
                    backgroundColor: theme.palette.action.hover,
                  }),
                }}
              >
                <TableCell>{organizer.platform_comission * 100 + ' %'}</TableCell>
                <TableCell>{organizer.seat_price + ' Lei'}</TableCell>
                <TableCell>{organizer.free_seat_price + ' Lei'}</TableCell>
                <TableCell>{organizer.cancel_seat_price + ' Lei'}</TableCell>
                <TableCell>{!!organizer.comission_cash_per_seat ? 'Yes' : 'No'}</TableCell>
                <TableCell>{organizer.notification_sms_price + ' Lei'}</TableCell>
                <TableCell>{organizer.notification_email_price + ' Lei'}</TableCell>
                <TableCell>{!!organizer.is_fiscal_serial_unique ? 'Yes' : 'No'}</TableCell>
                <TableCell>{!!organizer.is_zero_price_fiscalized ? 'Yes' : 'No'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography gutterBottom variant="h5">
            {t('Companies')}
          </Typography>
        </Grid>
        <TableContainer sx={{ marginTop: 2 }} component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ '& > th': { whiteSpace: 'nowrap' } }}>
                <TableCell>{t('Name')}</TableCell>
                <TableCell>{t('Tax Code')}</TableCell>
                <TableCell>{t('Reg. Code')}</TableCell>
                <TableCell>{t('VAT Payer')}</TableCell>
                <TableCell>{t('Address')}</TableCell>
                <TableCell>{t('City')}</TableCell>
                <TableCell>{t('County')}</TableCell>
                <TableCell>{t('Representative')}</TableCell>
                <TableCell>{t('Fiscal Serial Prefix')}</TableCell>
                <TableCell>{t('Contract ID')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {organizer.companies.map(
                ({
                  id,
                  company_name,
                  tax_code,
                  reg_code,
                  is_vat_payer,
                  address,
                  city,
                  county,
                  representative_full_name,
                  fiscal_serial_prefix,
                  contract_id,
                }) => (
                  <TableRow
                    key={id}
                    sx={{
                      '& > td': { whiteSpace: 'nowrap' },
                      '&:last-child td, &:last-child th': { borderBottom: 0 },
                      '&:nth-of-type(odd)': (theme) => ({
                        backgroundColor: theme.palette.action.hover,
                      }),
                    }}
                  >
                    <TableCell>{company_name}</TableCell>
                    <TableCell>{tax_code}</TableCell>
                    <TableCell>{reg_code || '-'}</TableCell>
                    <TableCell>{!!is_vat_payer ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{address}</TableCell>
                    <TableCell>{city}</TableCell>
                    <TableCell>{county}</TableCell>
                    <TableCell>{representative_full_name}</TableCell>
                    <TableCell>{fiscal_serial_prefix}</TableCell>
                    <TableCell>{contract_id}</TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>
      <LinkPaymentPointDialog open={openPaymentPoints} organizer={organizer} onClose={togglePaymentPoints} />
      <LinkVenueDialog open={openVenues} organizer={organizer} onClose={toggleVenues} />
      <LinkUserDialog open={openUsers} organizer={organizer} onClose={toggleUsers} />
      <OrganizerDrawer open={!!editOrganizer} organizer={editOrganizer} onClose={closeEdit} />
    </div>
  );
}
